/**
 * The one schema file of analytics events (`27_analytics_experiments.md` ANA-020).
 *
 * GENERATED by `scripts/generate-schema.ts` from the register in `27` §3.3. Do not edit by
 * hand: change `27`, run `pnpm --filter @fairway/analytics schema:generate`, and commit both.
 * `test/register.test.ts` fails while this file and `27` differ.
 *
 * Each event lists only its extra properties; every event also carries the envelope
 * (ANA-010, `envelope.ts`) and every client event the super-properties below (`24` §9).
 * A row of `27` that registers two or more events lists the union of its properties for each.
 */

import type { EventSpec } from './types';

/** Every event that `27` registers, by name. */
export const EVENTS = {
  signup_started: {
    table: 'Onboarding',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      method: {},
      source: { values: ['watch', 'clip', 'friend_link'] },
      source_ref: {},
    },
  },
  signup_completed: {
    table: 'Onboarding',
    src: 'server',
    system: false,
    admin: false,
    properties: { method: {}, referral_code: {} },
  },
  start_path_selected: {
    table: 'Onboarding',
    src: 'client',
    system: false,
    admin: false,
    properties: { start_path: {} },
  },
  golfer_created: {
    table: 'Onboarding',
    src: 'server',
    system: false,
    admin: false,
    properties: { start_path: {}, nation: {}, gender: {} },
  },
  tutorial_step_viewed: {
    table: 'Onboarding',
    src: 'client',
    system: false,
    admin: false,
    properties: { step_id: {} },
  },
  tutorial_completed: {
    table: 'Onboarding',
    src: 'client',
    system: false,
    admin: false,
    properties: { skipped: {} },
  },
  guest_converted: {
    table: 'Onboarding',
    src: 'server',
    system: false,
    admin: false,
    properties: { prior_distinct_id_hash: {} },
  },
  login_succeeded: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: { method: {} },
  },
  login_failed: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: { method: {}, reason: {} },
  },
  account_linked: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: { provider: {} },
  },
  parent_link_invited: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  parent_link_confirmed: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  parent_link_removed: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  account_deletion_requested: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: { days_since_creation: {} },
  },
  account_deletion_cancelled: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: { days_elapsed: {}, days_since_creation: {} },
  },
  account_closed: {
    table: 'Account',
    src: 'server',
    system: false,
    admin: false,
    properties: { days_since_creation: {} },
  },
  golfer_retired: {
    table: 'Golfer',
    src: 'server',
    system: false,
    admin: false,
    properties: { career_seasons: {}, kind: {}, level_band: {} },
  },
  family_link_golfer_started: {
    table: 'Golfer',
    src: 'server',
    system: false,
    admin: false,
    properties: { generation: {}, generations_on: {}, start_path: {}, predecessor_seasons: {} },
  },
  skill_milestone_reached: {
    table: 'Golfer',
    src: 'server',
    system: false,
    admin: false,
    properties: { skill: {}, grade: {} },
  },
  trait_revealed: {
    table: 'Golfer',
    src: 'client',
    system: false,
    admin: false,
    properties: { trait: {} },
  },
  potential_revealed: {
    table: 'Golfer',
    src: 'client',
    system: false,
    admin: false,
    properties: { skill_area: {} },
  },
  caddie_selected: {
    table: 'Golfer',
    src: 'client',
    system: false,
    admin: false,
    properties: { caddie_id: {}, context: { values: ['first_day', 'switch'] } },
  },
  training_plan_saved: {
    table: 'Training',
    src: 'server',
    system: false,
    admin: false,
    properties: { plan_hash: {} },
  },
  training_session_completed: {
    table: 'Training',
    src: 'server',
    system: false,
    admin: false,
    properties: { hours: {}, category: {} },
  },
  rested_bonus_applied: {
    table: 'Training',
    src: 'server',
    system: false,
    admin: false,
    properties: { days_banked: {} },
  },
  injury_started: {
    table: 'Training',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, severity: {}, area: {}, acwr_band: {} },
  },
  injury_recovered: {
    table: 'Training',
    src: 'server',
    system: false,
    admin: false,
    properties: { days_out: {} },
  },
  coach_hired: {
    table: 'Training',
    src: 'server',
    system: false,
    admin: false,
    properties: { coach_tier: {}, kind: {}, level: {}, specialities: {} },
  },
  facility_upgraded: {
    table: 'Training',
    src: 'server',
    system: false,
    admin: false,
    properties: { facility: {}, level: {}, currency: {} },
  },
  round_started: {
    table: 'Rounds',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      mode: {},
      kind: {},
      course_id: {},
      play_style: { values: ['caddie', 'key_moments', 'every_shot'] },
      light: { values: ['daylight', 'dusk', 'floodlit', 'glow'] },
      caddie_level: {},
      caddie_mood_band: {},
      caddie_energy_band: {},
      choice_depth: { values: ['simple', 'standard', 'full'] },
      started_by: { values: ['player', 'autopilot', 'autoplay'] },
    },
  },
  round_completed: {
    table: 'Rounds',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      gross: {},
      net: {},
      holes: {},
      decisions_answered: {},
      play_style: {},
      minutes: {},
      shots_one_tap: {},
      shots_changed: {},
      reads_used: {},
      moments_answered: {},
      moments_defaulted: {},
      time_bank_used_s: {},
      time_bank_empty_shots: {},
      focus_spent: {},
      receipt_opens: {},
      receipt_auto_opens: {},
      started_by: {},
    },
  },
  round_abandoned: {
    table: 'Rounds',
    src: 'server',
    system: false,
    admin: false,
    properties: { holes_played: {} },
  },
  autopilot_engaged: {
    table: 'Rounds',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {} },
  },
  replay_viewed: {
    table: 'Rounds',
    src: 'client',
    system: false,
    admin: false,
    properties: { replay_seconds_watched: {} },
  },
  live_round_joined: {
    table: 'Live',
    src: 'server',
    system: false,
    admin: false,
    properties: { field_size: {} },
  },
  decision_prompted: {
    table: 'Live',
    src: 'server',
    system: false,
    admin: false,
    properties: { decision_id: {}, hole: {}, stop_kind: { values: ['hole_plan', 'shot', 'putt'] } },
  },
  decision_answered: {
    table: 'Live',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      decision_id: {},
      latency_ms: {},
      source: { values: ['player', 'routine', 'timeout'] },
    },
  },
  spectate_started: {
    table: 'Live',
    src: 'client',
    system: false,
    admin: false,
    properties: { target_round_id: {} },
  },
  spectate_left: {
    table: 'Live',
    src: 'client',
    system: false,
    admin: false,
    properties: { target_round_id: {} },
  },
  fl_handicap_updated: {
    table: 'Handicap',
    src: 'server',
    system: false,
    admin: false,
    properties: { old: {}, new: {}, trigger: {} },
  },
  supplementary_card_declared: {
    table: 'Handicap',
    src: 'server',
    system: false,
    admin: false,
    properties: { mode: {}, course_tier: {} },
  },
  card_excluded: {
    table: 'Handicap',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason_code: {} },
  },
  flag_raised: {
    table: 'Handicap',
    src: 'server',
    system: false,
    admin: false,
    properties: { level: {} },
  },
  appeal_submitted: {
    table: 'Handicap',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      outcome: {},
      appeal_kind: { values: ['handicap', 'moderation', 'takedown', 'parent_code'] },
      original_action: {},
    },
  },
  appeal_resolved: {
    table: 'Handicap',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      outcome: {},
      appeal_kind: { values: ['handicap', 'moderation', 'takedown', 'parent_code'] },
      original_action: {},
    },
  },
  event_entered: {
    table: 'Competitions',
    src: 'server',
    system: false,
    admin: false,
    properties: { event_id: {}, tier: {} },
  },
  event_withdrawn: {
    table: 'Competitions',
    src: 'server',
    system: false,
    admin: false,
    properties: { event_id: {}, tier: {} },
  },
  cut_result: {
    table: 'Competitions',
    src: 'server',
    system: false,
    admin: false,
    properties: { made_cut: {} },
  },
  event_finished: {
    table: 'Competitions',
    src: 'server',
    system: false,
    admin: false,
    properties: { position: {}, field_size: {} },
  },
  prize_awarded: {
    table: 'Competitions',
    src: 'server',
    system: false,
    admin: false,
    properties: { currency: {}, amount: {} },
  },
  tour_school_entered: {
    table: 'Careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { stage: {} },
  },
  tour_card_earned: {
    table: 'Careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { tour: {} },
  },
  squad_call_up: {
    table: 'Careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { squad: {} },
  },
  milestone_reached: {
    table: 'Careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { milestone_id: {} },
  },
  trophy_awarded: {
    table: 'Careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { trophy_id: {} },
  },
  club_joined: {
    table: 'Clubs',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: { values: ['home', 'country'] } },
  },
  club_left: {
    table: 'Clubs',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: { values: ['home', 'country'] } },
  },
  club_takeover_started: {
    table: 'Clubs',
    src: 'server',
    system: false,
    admin: false,
    properties: { payment_method: {}, club_id: {}, tier: {}, valuation_coins: {} },
  },
  club_takeover_completed: {
    table: 'Clubs',
    src: 'server',
    system: false,
    admin: false,
    properties: { payment_method: {}, club_id: {}, tier: {}, valuation_coins: {} },
  },
  facility_built: {
    table: 'Clubs',
    src: 'server',
    system: false,
    admin: false,
    properties: { facility: {}, level: {} },
  },
  staff_hired: {
    table: 'Clubs',
    src: 'server',
    system: false,
    admin: false,
    properties: { role: {} },
  },
  role_assigned: {
    table: 'Roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { role: {} },
  },
  role_resigned: {
    table: 'Roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { role: {} },
  },
  coach_client_assigned: {
    table: 'Roles',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  club_manager_action: {
    table: 'Roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { action_type: {} },
  },
  item_equipped: {
    table: 'Equipment',
    src: 'client',
    system: false,
    admin: false,
    properties: { slot: {}, item_id: {} },
  },
  item_bought: {
    table: 'Equipment',
    src: 'server',
    system: false,
    admin: false,
    properties: { source: { values: ['coins', 'aces'] }, price: {} },
  },
  item_sold: {
    table: 'Equipment',
    src: 'server',
    system: false,
    admin: false,
    properties: { source: { values: ['coins', 'aces'] }, price: {} },
  },
  kit_fitting_completed: {
    table: 'Equipment',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      improvement_pct: {},
      fit_score_before: {},
      fit_score_after: {},
      provider: { values: ['computer', 'player_business'] },
    },
  },
  item_wear_warning: {
    table: 'Equipment',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      item_id: {},
      wear_pct: {},
      category: { values: ['grooves', 'grips', 'shoes', 'glove', 'balls'] },
    },
  },
  listing_created: {
    table: 'Economy',
    src: 'server',
    system: false,
    admin: false,
    properties: { item_category: {} },
  },
  auction_bid_placed: {
    table: 'Economy',
    src: 'server',
    system: false,
    admin: false,
    properties: { item_category: {}, final_price: {}, extended: {} },
  },
  auction_won: {
    table: 'Economy',
    src: 'server',
    system: false,
    admin: false,
    properties: { item_category: {}, final_price: {} },
  },
  price_index_viewed: {
    table: 'Economy',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  central_bank_change_applied: {
    table: 'Economy',
    src: 'server',
    system: false,
    admin: false,
    properties: { key: {}, old: {}, new: {} },
  },
  gift_sent: {
    table: 'Economy',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      funded_with: { values: ['coins', 'aces'] },
      value_band: { values: ['under_500', '500_to_1999', '2000_plus'] },
    },
  },
  paywall_viewed: {
    table: 'Purchases',
    src: 'client',
    system: false,
    admin: false,
    properties: { placement: {} },
  },
  purchase_started: {
    table: 'Purchases',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      sku: {},
      price_minor: {},
      currency: {},
      store: {},
      reason: {},
      first_purchase: {},
    },
  },
  purchase_completed: {
    table: 'Purchases',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      sku: {},
      price_minor: {},
      currency: {},
      store: {},
      reason: {},
      first_purchase: {},
    },
  },
  purchase_failed: {
    table: 'Purchases',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      sku: {},
      price_minor: {},
      currency: {},
      store: {},
      reason: {},
      first_purchase: {},
    },
  },
  subscription_started: {
    table: 'Purchases',
    src: 'server',
    system: false,
    admin: false,
    properties: { tier: {}, period: {}, trial: {} },
  },
  subscription_cancelled: {
    table: 'Purchases',
    src: 'server',
    system: false,
    admin: false,
    properties: { tier: {}, reason_bucket: {}, days_active: {} },
  },
  trial_started: { table: 'Purchases', src: 'server', system: false, admin: false, properties: {} },
  trial_converted: {
    table: 'Purchases',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  refund_issued: {
    table: 'Purchases',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason_bucket: {} },
  },
  feed_post_created: {
    table: 'Media and social',
    src: 'client',
    system: false,
    admin: false,
    properties: { post_kind: {}, audience: {}, has_snapshot: {}, moderation_state: {} },
  },
  reaction_added: {
    table: 'Media and social',
    src: 'client',
    system: false,
    admin: false,
    properties: { post_kind: {}, author_is_ai: {} },
  },
  comment_added: {
    table: 'Media and social',
    src: 'client',
    system: false,
    admin: false,
    properties: { post_kind: {}, author_is_ai: {} },
  },
  friend_request_sent: {
    table: 'Media and social',
    src: 'server',
    system: false,
    admin: false,
    properties: { source: {}, needed_parent: {} },
  },
  friend_request_accepted: {
    table: 'Media and social',
    src: 'server',
    system: false,
    admin: false,
    properties: { source: {}, needed_parent: {} },
  },
  news_article_viewed: {
    table: 'Media and social',
    src: 'client',
    system: false,
    admin: false,
    properties: { article_id: {}, ai_generated: {}, section: {}, story_type: {} },
  },
  show_episode_played: {
    table: 'Media and social',
    src: 'client',
    system: false,
    admin: false,
    properties: { episode_id: {}, seconds_listened: {}, percent: {}, kind: {}, captions_on: {} },
  },
  show_transcript_opened: {
    table: 'Media and social',
    src: 'client',
    system: false,
    admin: false,
    properties: { episode_id: {}, kind: {} },
  },
  weekly_recap_viewed: {
    table: 'Media and social',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      source: {},
      recap: { values: ['week', 'season'] },
      blocks_count: {},
      blocks_chosen: {},
      text_source: { values: ['ai', 'template'] },
    },
  },
  weekly_recap_shared: {
    table: 'Media and social',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      source: {},
      recap: { values: ['week', 'season'] },
      blocks_count: {},
      blocks_chosen: {},
      text_source: { values: ['ai', 'template'] },
    },
  },
  daily_checkin_completed: {
    table: 'Engagement',
    src: 'server',
    system: false,
    admin: false,
    properties: { streak_len: {} },
  },
  streak_broken: {
    table: 'Engagement',
    src: 'server',
    system: false,
    admin: false,
    properties: { prior_len: {} },
  },
  challenge_completed: {
    table: 'Engagement',
    src: 'server',
    system: false,
    admin: false,
    properties: { challenge_id: {} },
  },
  season_pass_tier_purchased: {
    table: 'Engagement',
    src: 'server',
    system: false,
    admin: false,
    properties: { price_minor: {}, half: { values: ['front', 'back'] } },
  },
  achievement_unlocked: {
    table: 'Engagement',
    src: 'server',
    system: false,
    admin: false,
    properties: { achievement_id: {} },
  },
  loyalty_tier_changed: {
    table: 'Engagement',
    src: 'server',
    system: false,
    admin: false,
    properties: { old: {}, new: {} },
  },
  notification_sent: {
    table: 'Notifications',
    src: 'server/client',
    system: false,
    admin: false,
    properties: { type: {}, channel: {} },
  },
  notification_opened: {
    table: 'Notifications',
    src: 'server/client',
    system: false,
    admin: false,
    properties: { type: {}, channel: {} },
  },
  notification_opt_out_changed: {
    table: 'Notifications',
    src: 'client',
    system: false,
    admin: false,
    properties: { channel: {}, enabled: {} },
  },
  quiet_hours_changed: {
    table: 'Notifications',
    src: 'client',
    system: false,
    admin: false,
    properties: { start: {}, end: {} },
  },
  report_submitted: {
    table: 'Safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { target_type: {}, reason_code: {}, severity: {} },
  },
  block_created: {
    table: 'Safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { surface: {}, mute_kind: {} },
  },
  mute_created: {
    table: 'Safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { surface: {}, mute_kind: {} },
  },
  parental_control_changed: {
    table: 'Safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { control: {}, enabled: {} },
  },
  break_reminder_shown: {
    table: 'Safety',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  age_check_started: {
    table: 'Safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { method: {}, result: {}, resulting_band: {}, feature: {} },
  },
  age_check_completed: {
    table: 'Safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { method: {}, result: {}, resulting_band: {}, feature: {} },
  },
  moderation_action_taken: {
    table: 'Safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { action: {}, reason_category: {}, decided_by: { values: ['auto', 'person'] } },
  },
  unit_system_changed: {
    table: 'Settings',
    src: 'client',
    system: false,
    admin: false,
    properties: { to: {} },
  },
  language_changed: {
    table: 'Settings',
    src: 'client',
    system: false,
    admin: false,
    properties: { to: {} },
  },
  accessibility_setting_changed: {
    table: 'Settings',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      setting: {},
      value: {},
      old_value: {},
      source: { values: ['onboarding', 'settings', 'quick_toggle'] },
    },
  },
  data_export_requested: {
    table: 'Settings',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  data_export_ready: {
    table: 'Settings',
    src: 'server',
    system: false,
    admin: false,
    properties: { duration_s: {} },
  },
  app_launched: {
    table: 'Performance',
    src: 'client',
    system: false,
    admin: false,
    properties: { kind: {}, duration_ms: {} },
  },
  screen_load_time: {
    table: 'Performance',
    src: 'client',
    system: false,
    admin: false,
    properties: { screen: {}, duration_ms: {} },
  },
  api_error: {
    table: 'Performance',
    src: 'client',
    system: false,
    admin: false,
    properties: { endpoint: {}, status_code: {} },
  },
  crash_logged: {
    table: 'Performance',
    src: 'server',
    system: false,
    admin: false,
    properties: { fatal: {} },
  },
  frame_drop_session: {
    table: 'Performance',
    src: 'client',
    system: false,
    admin: false,
    properties: { avg_fps: {}, surface: { values: ['hole_map', 'broadcast_view', 'other'] } },
  },
  screen_viewed: {
    table: 'Screens',
    src: 'client',
    system: false,
    admin: false,
    properties: { screen_code: {} },
  },
  hole_completed: {
    table: 'The decision system',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      hole: {},
      par: {},
      strokes: {},
      play_style: {},
      plan_route: { values: ['card_1', 'card_2', 'card_3', 'own', 'routine', 'caddie'] },
      plan_cards_shown: {},
      plan_card_detail: { values: ['words', 'trouble', 'expected_score'] },
      plan_b_used: {},
      reads_used: {},
      reads_by_kind: {},
      shots_one_tap: {},
      shots_changed: {},
      choices_changed_by_kind: {},
      extra_care_used: {},
      caddie_asked: {},
      caddie_overruled: {},
      quick_actions: {},
      think_time_s: {},
      focus_left: {},
      time_bank_left_s: {},
      plan_seconds: {},
      plan_from_book: { boolean: true },
    },
  },
  moment_resolved: {
    table: 'The decision system',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      trigger: {
        values: [
          'bad_hole',
          'hot_run',
          'tired_legs',
          'weather_turns',
          'slow_play',
          'on_the_clock',
          'niggle',
          'last_six_holes',
          'match_play',
          'ruling',
        ],
      },
      option: {},
      source: { values: ['player', 'standing_order', 'timeout'] },
      latency_ms: {},
      hole: {},
    },
  },
  time_bank_emptied: {
    table: 'The decision system',
    src: 'server',
    system: false,
    admin: false,
    properties: { hole: {}, play_style: {} },
  },
  pace_warning_given: {
    table: 'The decision system',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: { values: ['warning', 'penalty'] }, hole: {} },
  },
  routine_saved: {
    table: 'The decision system',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      part: { values: ['shot_defaults', 'standing_reads', 'standing_orders', 'stop_me_rules'] },
      source: { values: ['editor', 'after_shot', 'caddie_suggestion'] },
    },
  },
  play_style_changed: {
    table: 'The decision system',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      from: {},
      to: {},
      where: { values: ['before_round', 'in_round', 'settings'] },
      hole: {},
    },
  },
  shot_receipt_opened: {
    table: 'The decision system',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      when: { values: ['in_round', 'after_round'] },
      shot_kind: { values: ['tee', 'approach', 'short_game', 'putt'] },
      what_else_viewed: {},
      opened_by: { values: ['why_button', 'learning_mode'] },
    },
  },
  shot_played: {
    table: 'The decision system',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      hole: {},
      shot_no: {},
      shot_kind: {},
      club: {},
      target_moved: {},
      shape: {},
      height: {},
      power: {},
      grip_down: {},
      spin: {},
      tee_height: {},
      tee_side: {},
      allow_for_lie: {},
      mindset: {},
      routine_length: {},
      swing_thought: {},
      breathing: {},
      extra_care: {},
      short_game_type: {},
      putt_pace: {},
      putt_intent: {},
      reads: {},
      one_tap: {},
      caddie_advice_followed: {},
      think_time_s: {},
    },
  },
  week_plan_approved: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      days_planned: {},
      golf_hours_planned: {},
      entries: {},
      rest_days: {},
      by: { values: ['player', 'autoplay'] },
    },
  },
  prep_choice_set: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      event_id: {},
      kind: {
        values: ['study', 'practice_round', 'caddie', 'brief', 'kit', 'food', 'sleep', 'warm_up'],
      },
      by: { values: ['player', 'caddie', 'coach', 'autoplay'] },
    },
  },
  boldness_set: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      event_id: {},
      boldness: { values: ['bold', 'balanced', 'safe'] },
      by: { values: ['player', 'caddie'] },
    },
  },
  round_report_opened: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      round_id: {},
      when: { values: ['live', 'after'] },
      detail: { values: ['simple', 'standard', 'full'] },
    },
  },
  report_difference_opened: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'client',
    system: false,
    admin: false,
    properties: { round_id: {}, lines: {}, replay_quoted: {} },
  },
  report_breakdown_opened: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'client',
    system: false,
    admin: false,
    properties: { round_id: {}, holes_opened: {} },
  },
  daily_decision_made: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'server',
    system: false,
    admin: false,
    properties: { decision_id: {}, option: {} },
  },
  daily_decision_result_viewed: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'client',
    system: false,
    admin: false,
    properties: { decision_id: {} },
  },
  life_moment_resolved: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, option: {}, source: { values: ['player', 'autoplay'] } },
  },
  friend_challenge_result_viewed: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'client',
    system: false,
    admin: false,
    properties: { match_id: {}, side: { values: ['host', 'guest'] }, prep_choices: {} },
  },
  atlas_picture_opened: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'client',
    system: false,
    admin: false,
    properties: { where: { values: ['course_page', 'study', 'travel', 'ownership', 'building'] } },
  },
  range_night_entered: {
    table: 'The new loop: plans, preparation, live events and round reports',
    src: 'server',
    system: false,
    admin: false,
    properties: { action: { values: ['join', 'leave'] } },
  },
  caddie_level_ready: {
    table: 'Caddies',
    src: 'server',
    system: false,
    admin: false,
    properties: { to_level: {}, rounds_together: {} },
  },
  caddie_level_up: {
    table: 'Caddies',
    src: 'server',
    system: false,
    admin: false,
    properties: { from_level: {}, to_level: {}, currency: { values: ['coins', 'aces'] } },
  },
  caddie_share_set: {
    table: 'Caddies',
    src: 'server',
    system: false,
    admin: false,
    properties: { share_pct: {} },
  },
  caddie_practice_advice_shown: {
    table: 'Caddies',
    src: 'server',
    system: false,
    admin: false,
    properties: { sg_area: {}, caddie_level: {} },
  },
  caddie_practice_advice_added: {
    table: 'Caddies',
    src: 'server',
    system: false,
    admin: false,
    properties: { sg_area: {} },
  },
  caddie_practice_advice_undone: {
    table: 'Caddies',
    src: 'server',
    system: false,
    admin: false,
    properties: { sg_area: {} },
  },
  extra_hours_bought: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      hours: {},
      currency: {},
      day_kind: { values: ['weekday', 'weekend', 'school_holiday'] },
    },
  },
  support_item_bought: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, currency: {} },
  },
  prep_block_bought: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      item: {
        values: [
          'coach_week',
          'coach_lesson',
          'physio_visit',
          'mind_coach_week',
          'nutrition_week',
          'camp_week',
          'extra_hour',
          'fitting_shop',
          'fitting_van',
        ],
      },
      level: {},
      units: {},
    },
  },
  limit_lift_earned: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { skill: {}, lift_total: {} },
  },
  camp_bonus_started: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { provider: { values: ['computer', 'player_business'] }, days: {} },
  },
  paid_cap_applied: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { scale_band: { values: ['0.75_to_1', '0.5_to_0.75', 'under_0.5'] } },
  },
  support_ended: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, weeks: {} },
  },
  homegrown_badge_lost: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { lever: {}, route: { values: ['purchase', 'use'] } },
  },
  homegrown_league_entered: {
    table: 'Training and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { event_id: {} },
  },
  fair_play_page_viewed: {
    table: 'Training and fair play',
    src: 'client',
    system: false,
    admin: false,
    properties: { entry: {} },
  },
  item_replaced: {
    table: 'Wear and fit',
    src: 'server',
    system: false,
    admin: false,
    properties: { category: {}, wear_pct: {}, currency: {} },
  },
  refit_advice_shown: {
    table: 'Wear and fit',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      club_category: {},
      cause: { values: ['growth', 'training', 'ageing'] },
      cost_yards: {},
      lead_component: {},
      drift_points: {},
      cost_shots: {},
    },
  },
  refit_advice_acted: {
    table: 'Wear and fit',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      club_category: {},
      days_since_advice: {},
      provider: { values: ['computer', 'player_business'] },
    },
  },
  refit_advice_dismissed: {
    table: 'Wear and fit',
    src: 'client',
    system: false,
    admin: false,
    properties: { club_category: {} },
  },
  spend_statement_sent: {
    table: 'Spending safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { channels: {} },
  },
  spend_statement_viewed: {
    table: 'Spending safety',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  spend_check_shown: {
    table: 'Spending safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { threshold_gbp: {} },
  },
  spend_check_answered: {
    table: 'Spending safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { threshold_gbp: {}, choice: { values: ['carry_on', 'set_limit'] } },
  },
  spend_limit_changed: {
    table: 'Spending safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { action: { values: ['set', 'lowered', 'raised', 'removed'] } },
  },
  u18_spending_switched: {
    table: 'Spending safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { enabled: {} },
  },
  purchase_blocked: {
    table: 'Spending safety',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {} },
  },
  business_opened: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, currency: {}, partners: {} },
  },
  business_partner_changed: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, action: { values: ['joined', 'left'] } },
  },
  business_research_completed: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, days: {} },
  },
  business_price_set: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, price_ratio_to_ref: {} },
  },
  business_purchase_made: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, owner_kind: { values: ['player', 'computer'] }, price_coins: {} },
  },
  business_day_summarised: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      type: {},
      sales_real: {},
      sales_computer: {},
      revenue_coins: {},
      game_cut_coins: {},
      reputation_band: {},
    },
  },
  business_sponsorship_signed: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { target: { values: ['golfer', 'club', 'event'] }, length_days: {} },
  },
  business_sponsorship_lapsed: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { target: { values: ['golfer', 'club', 'event'] } },
  },
  business_closed: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      type: {},
      owner_kind: {},
      reason: { values: ['owner_choice', 'computer_retired', 'moderation', 'other'] },
    },
  },
  resort_trip_booked: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { group_size: {}, nights: {} },
  },
  club_extra_built: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { extra: {}, target: {}, funded_with: {}, rushed: {} },
  },
  club_share_sold: {
    table: 'Businesses and club extras',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, share_pct: {}, price_coins: {}, grassroots_diversion_coins: {} },
  },
  land_map_opened: {
    table: 'Land and courses',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  plot_viewed: {
    table: 'Land and courses',
    src: 'client',
    system: false,
    admin: false,
    properties: { is_hot: {}, price_band: {} },
  },
  plot_unavailable_shown: {
    table: 'Land and courses',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  plot_bought: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { currency: {}, price_band: {} },
  },
  plot_auction_bid_placed: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { extended: {} },
  },
  plot_auction_closed: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { reserve_met: {}, bidders: {}, extensions: {}, price_aces: {} },
  },
  course_design_started: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { method: { values: ['ai_designer', 'ready_made'] } },
  },
  course_design_adjusted: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { changes: {} },
  },
  course_opened: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { method: {}, days_from_plot: {} },
  },
  hosting_bid_placed: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { event_tier: {} },
  },
  hosting_right_won: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { event_tier: {} },
  },
  plot_surveyed: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { holes_possible: {}, build_band: {} },
  },
  plot_watched: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  designer_concepts_shown: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { count: {}, fallback: {} },
  },
  ready_made_fit_result: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { result: {} },
  },
  course_build_started: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      build_standard: {},
      crew: {},
      build_days: {},
      funded_with: { values: ['coins', 'aces'] },
    },
  },
  plot_returned: {
    table: 'Land and courses',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {} },
  },
  family_mode_started: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { tier: {} },
  },
  family_junior_created: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { juniors: {}, level_max: {} },
  },
  junior_place_changed: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      to: { values: ['carrying_on', 'paused'] },
      reason: { values: ['level_fell', 'level_rose', 'place_free', 'player_choice'] },
    },
  },
  junior_week_planned: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { practice_share_band: {}, fun_share_band: {}, week_balance: {} },
  },
  junior_enjoyment_changed: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { from_band: {}, to_band: {} },
  },
  junior_burnout_started: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { days: {} },
  },
  junior_burnout_ended: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { days: {} },
  },
  junior_event_entered: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { event_tier: {} },
  },
  junior_away_week_closed: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { floor_acted: {}, enjoyment_band: {} },
  },
  family_mode_paused: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: { values: ['membership_ended', 'player_choice', 'feature_off'] } },
  },
  family_mode_resumed: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: { values: ['membership_ended', 'player_choice', 'feature_off'] } },
  },
  handover_previewed: {
    table: 'Family mode',
    src: 'previewed: client; completed: server',
    system: false,
    admin: false,
    properties: {
      junior_age: {},
      seasons_in_family_mode: {},
      older_outcome: {},
      juniors_staying: {},
    },
  },
  handover_completed: {
    table: 'Family mode',
    src: 'previewed: client; completed: server',
    system: false,
    admin: false,
    properties: {
      junior_age: {},
      seasons_in_family_mode: {},
      older_outcome: {},
      juniors_staying: {},
    },
  },
  handover_refused: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { code: {} },
  },
  legacy_points_awarded: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {}, points: {}, tier: {} },
  },
  legacy_tier_reached: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {}, points: {}, tier: {} },
  },
  family_scrapbook_viewed: {
    table: 'Family mode',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  scrapbook_page_shared: {
    table: 'Family mode',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, audience: { values: ['friends', 'dm', 'outside'] }, junior_shown: {} },
  },
  watch_link_created: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: { round_kind: {} },
  },
  watch_page_viewed: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      watch_link_id: {},
      live: {},
      referrer_kind: { values: ['direct', 'social', 'search', 'other'] },
    },
  },
  watch_visit_ended: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: { watch_link_id: {}, seconds_band: {} },
  },
  watch_join_tapped: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: { watch_link_id: {} },
  },
  clip_created: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: { moment_kind: {} },
  },
  clip_shared: {
    table: 'Watching',
    src: 'client',
    system: false,
    admin: false,
    properties: { channel: {} },
  },
  clip_viewed: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: { clip_id: {} },
  },
  broadcast_view_opened: {
    table: 'Watching',
    src: 'client',
    system: false,
    admin: false,
    properties: { where: { values: ['own_round', 'watching', 'replay'] } },
  },
  show_started: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      show_kind: { values: ['major', 'team_cup'] },
      mode: { values: ['live', 'as_live'] },
      minutes: {},
    },
  },
  show_ended: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      show_kind: { values: ['major', 'team_cup'] },
      mode: { values: ['live', 'as_live'] },
      minutes: {},
    },
  },
  spoiler_shield_set: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: { on: {}, where: { values: ['show_card', 'settings', 'show_me_now'] } },
  },
  watch_link_revoked: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: { by: { values: ['player', 'system', 'admin'] } },
  },
  clip_render_completed: {
    table: 'Watching',
    src: 'server',
    system: false,
    admin: false,
    properties: { format: {}, seconds_band: {}, ok: {} },
  },
  broadcast_view_perf_sampled: {
    table: 'Watching',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      avg_fps: {},
      low_fps: {},
      scene_build_ms: {},
      quality_step: {},
      device_class: {},
    },
  },
  show_recut: { table: 'Watching', src: 'server', system: false, admin: false, properties: {} },
  turn_summary_viewed: {
    table: 'Season and careers',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  break_week_event_entered: {
    table: 'Season and careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {} },
  },
  ranking_viewed: {
    table: 'Season and careers',
    src: 'client',
    system: false,
    admin: false,
    properties: { ranking: { values: ['ranking_pro', 'ranking_amateur'] } },
  },
  cup_team_selected: {
    table: 'Season and careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { cup: {}, route: { values: ['ranking', 'captains_pick'] } },
  },
  captain_vote_cast: {
    table: 'Season and careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { cup: {}, side: {} },
  },
  club_league_match_played: {
    table: 'Season and careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { division: {}, autopilot: {} },
  },
  pro_start_chosen: {
    table: 'Season and careers',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  friend_link_sent: {
    table: 'Season and careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { guest_golfer: {}, seconds_to_first_tee: {} },
  },
  friend_guest_joined: {
    table: 'Season and careers',
    src: 'server',
    system: false,
    admin: false,
    properties: { guest_golfer: {}, seconds_to_first_tee: {} },
  },
  closure_note_viewed: {
    table: 'Season and careers',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  score_for_cut_shown: {
    table: 'Season and careers',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      place: { values: ['pre_round', 'in_round', 'round_end'] },
      kind: { values: ['line', 'any', 'none'] },
      reason: {},
    },
  },
  gate_run_started: {
    table: 'Stage gates',
    src: 'client',
    system: false,
    admin: false,
    properties: { gate: { values: ['fun', 'look', 'decision', 'clarity', 'return'] }, stage: {} },
  },
  gate_answer_recorded: {
    table: 'Stage gates',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      gate: {},
      question: {
        values: [
          'fun',
          'enough_choices',
          'clear_why',
          'show_a_friend',
          'look_score',
          'clarity_reason',
          'would_return',
        ],
      },
      answer: { values: ['yes', 'sort_of', 'no', 'right', 'wrong'] },
      group: { values: ['founder', 'management_players', 'club_golfers', 'society'] },
      next_decision_named: {},
    },
  },
  hole_map_perf_sampled: {
    table: 'Stage gates',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      avg_fps: {},
      low_fps: {},
      first_paint_ms: {},
      layers_dropped: {},
      style: { values: ['painted', 'high_contrast', 'calm'] },
      reduced_motion: {},
      intro_skips: {},
      intro_skip_ms_median: {},
      read_animations_skipped: {},
      flat_plan_used: {},
      device_class: {},
      bounces_drawn_max: {},
    },
  },
  view_mode_previewed: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: { view: {}, step: {} },
  },
  pro_view_toggled: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: { screen_code: {}, scope: { values: ['global', 'screen'] }, value: {} },
  },
  celebration_shown: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: { kind: {}, duration_ms: {}, skipped: {} },
  },
  share_card_created: {
    table: 'The design layer',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      kind: {},
      template: {},
      format: {},
      screen_code: {},
      spoiler_safe: { boolean: true },
    },
  },
  share_card_shared: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      kind: {},
      template: {},
      format: {},
      destination: { values: ['system_share', 'feed'] },
    },
  },
  chart_table_view_opened: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: { chart: {} },
  },
  audio_channel_changed: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: { channel: {}, volume: {}, muted: {} },
  },
  live_activity_started: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      round_id: {},
      reason: { values: ['round_complete', 'user_dismissed', 'expired'] },
    },
  },
  live_activity_ended: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      round_id: {},
      reason: { values: ['round_complete', 'user_dismissed', 'expired'] },
    },
  },
  live_activity_action: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: { round_id: {}, action: {}, option_is_caddie_pick: {} },
  },
  widget_snapshot_served: {
    table: 'The design layer',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, family: {} },
  },
  hole_map_view_toggled: {
    table: 'The design layer',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      view: { values: ['tilted', 'flat'] },
      source: { values: ['view_menu', 'settings'] },
    },
  },
  help_opened: {
    table: 'Help, search and the age gate',
    src: 'client',
    system: false,
    admin: false,
    properties: { screen_code: {}, in_round: {} },
  },
  help_article_opened: {
    table: 'Help, search and the age gate',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      article_id: {},
      from: { values: ['search', 'category', 'caddie_help', 'assistant'] },
    },
  },
  help_human_requested: {
    table: 'Help, search and the age gate',
    src: 'client',
    system: false,
    admin: false,
    properties: { topic: { values: ['account', 'safety', 'payment', 'other'] } },
  },
  support_session_started: {
    table: 'Help, search and the age gate',
    src: 'client',
    system: false,
    admin: false,
    properties: { entry_point: {}, helpful: {} },
  },
  support_session_rated: {
    table: 'Help, search and the age gate',
    src: 'client',
    system: false,
    admin: false,
    properties: { entry_point: {}, helpful: {} },
  },
  support_handover: {
    table: 'Help, search and the age gate',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      trigger: { values: ['asked', 'topic', 'turns', 'failure', 'safety'] },
      turns: {},
    },
  },
  search_performed: {
    table: 'Help, search and the age gate',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      where: { values: ['global', 'help_centre'] },
      results_band: { values: ['0', '1_5', '6_plus'] },
      opened_type: { values: ['golfer', 'club', 'course', 'event', 'news', 'help'] },
      opened_rank: {},
    },
  },
  age_gate_passed: {
    table: 'Help, search and the age gate',
    src: 'server',
    system: false,
    admin: false,
    properties: { where: { values: ['onboarding', 'guest_save'] } },
  },
  admin_action_taken: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { command: {}, target_type: {}, role: {} },
  },
  config_change_approved: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: {},
  },
  moderation_item_resolved: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { severity: {}, action: {}, sla_met: {} },
  },
  support_ticket_resolved: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { category: {}, resolution_time_min: {} },
  },
  area_closed: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { category: {}, courses_affected: {} },
  },
  area_reopened: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { hours_closed: {}, partial: {} },
  },
  nogo_entry_changed: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { action: {}, category: {}, source: { values: ['data', 'manual'] } },
  },
  plot_auction_admin_action: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { action: { values: ['schedule', 'pause', 'cancel', 'void'] } },
  },
  business_moderated: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { action: {}, reason_code: {} },
  },
  watch_takedown: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: {
      target: { values: ['watch_link', 'clip', 'show'] },
      reason_code: {},
      actor_type: { values: ['admin', 'system'] },
    },
  },
  watch_restored: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { target: {} },
  },
  show_stopped: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { reason: {} },
  },
  purchases_held: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { reason_code: {} },
  },
  homegrown_corrected: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: {},
  },
  cost_warning_raised: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { kind: {} },
  },
  idea_decided: {
    table: 'Admin tools',
    src: 'server',
    system: true,
    admin: true,
    properties: { decision: { values: ['yes', 'no', 'parked'] } },
  },
  choice_depth_changed: {
    table: 'Choices, learning and the review',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      from: {},
      to: { values: ['simple', 'standard', 'full'] },
      in_round: { boolean: true },
    },
  },
  explain_shots_changed: {
    table: 'Choices, learning and the review',
    src: 'client',
    system: false,
    admin: false,
    properties: { to: { values: ['off', 'my_shots'] }, from_prompt: {} },
  },
  decision_review_viewed: {
    table: 'Choices, learning and the review',
    src: 'client',
    system: false,
    admin: false,
    properties: { total: {}, decisions: {} },
  },
  skills_attempt_played: {
    table: 'The skills challenge',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      skill: { values: ['long', 'close', 'in'] },
      attempt: {},
      points: {},
      practice: {},
      started_by: {},
      bold: {},
    },
  },
  autoplay_started: {
    table: 'Autoplay',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      days: {},
      notify_level: { values: ['everything', 'results', 'needs_me', 'nothing'] },
      standing_entries: { values: ['home_club_weekly', 'club_team', 'tour_next_events'] },
      budget_band: { values: ['zero', '1_to_500', '501_to_1500', '1501_plus'] },
      stop_rules: { values: ['injury', 'cut_or_final', 'major_soon'] },
    },
  },
  autoplay_changed: {
    table: 'Autoplay',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      what: {
        values: [
          'last_day',
          'usual_tee_time',
          'standing_entries',
          'budget',
          'notify_level',
          'stop_rules',
        ],
      },
    },
  },
  autoplay_ended: {
    table: 'Autoplay',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      reason: { values: ['last_day', 'player_off', 'stop_rule'] },
      days_run: {},
      player_active_days: {},
    },
  },
  autoplay_entry_made: {
    table: 'Autoplay',
    src: 'server',
    system: false,
    admin: false,
    properties: { route: { values: ['free', 'paid'] } },
  },
  autoplay_entry_passed_over: {
    table: 'Autoplay',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      reason: {
        values: [
          'over_limit',
          'not_enough_coins',
          'not_eligible',
          'field_full',
          'junior_week_full',
        ],
      },
    },
  },
  whats_on_opened: {
    table: 'The long sitting',
    src: 'client',
    system: false,
    admin: false,
    properties: { source: { values: ['done_for_today', 'play'] } },
  },
  whats_on_item_started: {
    table: 'The long sitting',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      kind: {
        values: [
          'entered_round',
          'live_event',
          'watch',
          'new_course',
          'long_job',
          'daily_decision',
        ],
      },
    },
  },
  open_to_game_set: {
    table: 'The long sitting',
    src: 'server',
    system: false,
    admin: false,
    properties: { on: {} },
  },
  play_on_offered: {
    table: 'The long sitting',
    src: 'server',
    system: false,
    admin: false,
    properties: { group_size: {} },
  },
  play_on_joined: {
    table: 'The long sitting',
    src: 'server',
    system: false,
    admin: false,
    properties: { group_size: {} },
  },
  ball_check_shown: {
    table: 'Ball fit',
    src: 'server',
    system: false,
    admin: false,
    properties: { fitter_level: {}, best_fit: {} },
  },
  ball_changed: {
    table: 'Ball fit',
    src: 'server',
    system: false,
    admin: false,
    properties: { fit_before: {}, fit_after: {}, after_ball_check: {} },
  },
  junior_ladder_step_reached: {
    table: 'Junior ladders and family pairs',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      event_code: { values: ['junior_skills', 'world_juniors', 'family_pairs'] },
      step: { values: ['county', 'national', 'world'] },
      age_group: {},
      via: { values: ['place', 'tie', 'fill'] },
    },
  },
  feed_viewed: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: { tab: {}, items_loaded: {} },
  },
  feed_caught_up: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: { tab: {}, items_seen: {} },
  },
  follow_created: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { target_type: { values: ['golfer', 'club'] }, target_is_ai: {} },
  },
  report_outcome_viewed: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: { outcome_code: {}, hours_to_outcome: {} },
  },
  chat_message_sent: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { channel_kind: {}, mode: { values: ['text', 'phrase'] }, held: {} },
  },
  dm_sent: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { has_card: {}, is_phrase: {} },
  },
  notice_posted: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { source: {}, type: {}, important: {} },
  },
  news_error_reported: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: { section: {} },
  },
  interview_answered: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { tone: {}, trigger_code: {}, hours_to_answer: {} },
  },
  share_link_opened: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, app_installed: {} },
  },
  review_submitted: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { target_kind: {}, stars: {}, has_text: {} },
  },
  review_reply_posted: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { target_kind: {}, stars: {}, has_text: {} },
  },
  rivalry_formed: {
    table: 'Media, social and community',
    src: 'formed: server; the others: client',
    system: false,
    admin: false,
    properties: { visibility: {}, pair_type: { values: ['hh', 'ha', 'aa'] } },
  },
  rivalry_viewed: {
    table: 'Media, social and community',
    src: 'formed: server; the others: client',
    system: false,
    admin: false,
    properties: { visibility: {}, pair_type: { values: ['hh', 'ha', 'aa'] } },
  },
  rivalry_hidden: {
    table: 'Media, social and community',
    src: 'formed: server; the others: client',
    system: false,
    admin: false,
    properties: { visibility: {}, pair_type: { values: ['hh', 'ha', 'aa'] } },
  },
  fame_band_changed: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { from: {}, to: {} },
  },
  fantasy_team_saved: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { is_major: {}, league_kind: {}, rank_pct: {} },
  },
  fantasy_league_joined: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { is_major: {}, league_kind: {}, rank_pct: {} },
  },
  fantasy_contest_scored: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { is_major: {}, league_kind: {}, rank_pct: {} },
  },
  creator_code_attached: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { creator_id: {} },
  },
  creator_code_removed: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: { creator_id: {} },
  },
  community_link_opened: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: { destination: { values: ['discord', 'facebook'] } },
  },
  feed_clip_played: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: { post_audience: {}, author_is_self: {}, watched_to_end: {} },
  },
  feed_watch_link_opened: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: { surface: { values: ['feed', 'dm', 'notice'] }, match_state: {} },
  },
  share_refused: {
    table: 'Media, social and community',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      object_type: { values: ['clip', 'watch_link', 'scrapbook_page'] },
      reason_code: {},
    },
  },
  video_show_promo_opened: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      surface: { values: ['home_card', 'feed_post', 'share_card'] },
      destination: { values: ['in_app', 'youtube'] },
    },
  },
  show_commentary_viewed: {
    table: 'Media, social and community',
    src: 'client',
    system: false,
    admin: false,
    properties: { event_kind: { values: ['major', 'team_cup'] }, lines_seen: {}, live: {} },
  },
  referral_sent: {
    table: 'Engagement and live-ops',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  referral_counted: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  society_created: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  society_trip_started: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  society_trip_completed: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  game_master_draft_created: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  game_master_event_approved: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  wellbeing_time_limit_set: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  happiness_survey_shown: {
    table: 'Engagement and live-ops',
    src: 'client',
    system: false,
    admin: false,
    properties: { score: {} },
  },
  happiness_survey_answered: {
    table: 'Engagement and live-ops',
    src: 'client',
    system: false,
    admin: false,
    properties: { score: {} },
  },
  season_pass_opened: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: { half: { values: ['front', 'back'] }, level_reached: {}, premium: {} },
  },
  season_pass_closed: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: { half: { values: ['front', 'back'] }, level_reached: {}, premium: {} },
  },
  turn_content_released: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  awards_published: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: { occasion: {} },
  },
  award_granted: {
    table: 'Engagement and live-ops',
    src: 'server',
    system: false,
    admin: false,
    properties: { award_code: {}, occasion: {}, scope: {} },
  },
  awards_presentation_opened: {
    table: 'Engagement and live-ops',
    src: 'client',
    system: false,
    admin: false,
    properties: { occasion: {}, seconds_after_publication: {} },
  },
  break_week_programme_viewed: {
    table: 'Engagement and live-ops',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  ai_content_viewed: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      task_id: {},
      surface: {},
      source: { values: ['ai', 'template', 'rules'] },
      template_version: {},
      style: {},
    },
  },
  ai_label_explainer_opened: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { surface: {} },
  },
  ai_highlights_toggled: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { enabled: {} },
  },
  commentary_style_changed: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { from: {}, to: {}, mid_round: {} },
  },
  caddie_tip_viewed: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { context: {}, source: {} },
  },
  coach_explanation_viewed: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { source: {}, regenerations_this_week: {} },
  },
  coach_explanation_regenerated: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { source: {}, regenerations_this_week: {} },
  },
  bio_paragraph_hidden: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { milestone_type: {} },
  },
  bio_paragraph_regenerated: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { milestone_type: {} },
  },
  rivalry_story_viewed: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { privacy: {} },
  },
  ai_feed_posts_toggled: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: { enabled: {} },
  },
  ai_output_reported: {
    table: 'AI text and its health',
    src: 'server',
    system: false,
    admin: false,
    properties: { task_id: {}, reason: {} },
  },
  welcome_back_viewed: {
    table: 'AI text and its health',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      days_away_bucket: { values: ['7_13', '14_29', '30_plus', 'autoplay_period'] },
      beats: {},
      source: {},
    },
  },
  ai_fallback_served: {
    table: 'AI text and its health',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      task_id: {},
      reason: {
        values: [
          'budget',
          'check_failed',
          'timeout',
          'provider',
          'kill_switch',
          'disabled',
          'sensitivity_pause',
          'show_budget',
        ],
      },
    },
  },
  parent_approval_requested: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, outcome: {} },
  },
  parent_approval_decided: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, outcome: {}, hours_to_decide: {} },
  },
  chat_message_blocked: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { channel_kind: {}, ai_score: {} },
  },
  chat_message_held: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { channel_kind: {}, ai_score: {} },
  },
  safeguarding_case_opened: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: true,
    admin: false,
    properties: {},
  },
  parent_code_viewed: {
    table: 'Safety, privacy and moderation',
    src: 'viewed: client; accepted: server',
    system: false,
    admin: false,
    properties: { version: {} },
  },
  parent_code_accepted: {
    table: 'Safety, privacy and moderation',
    src: 'viewed: client; accepted: server',
    system: false,
    admin: false,
    properties: { version: {} },
  },
  parent_code_action_taken: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { step: {}, reason_code: {} },
  },
  junior_phrase_limited: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { limit: { values: ['cooldown', 'round_max'] } },
  },
  watch_permission_changed: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { subject: { values: ['teen', 'own_junior'] }, mode: {} },
  },
  watch_blocked: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { surface: {}, reason: { values: ['protected', 'opted_out', 'not_allowed'] } },
  },
  public_report_submitted: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { surface: {}, reason_code: {} },
  },
  takedown_completed: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { surface: {}, trigger: {}, minutes_to_takedown: {} },
  },
  u18_contact_pattern_flagged: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: true,
    admin: false,
    properties: {},
  },
  guilt_lint_blocked: {
    table: 'Safety, privacy and moderation',
    src: 'server',
    system: false,
    admin: false,
    properties: { source: { values: ['copy', 'ai'] }, pattern_id: {} },
  },
  copy_variant_shown: {
    table: 'Words and help',
    src: 'client',
    system: false,
    admin: false,
    properties: { key: {}, variant: {}, experiment_id: {} },
  },
  content_lint_override: {
    table: 'Words and help',
    src: 'server',
    system: false,
    admin: false,
    properties: { key: {}, reason_code: {}, role: {} },
  },
  glossary_term_viewed: {
    table: 'Words and help',
    src: 'client',
    system: false,
    admin: false,
    properties: { term: {}, shown_in: {} },
  },
  course_status_changed: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { course_id: {}, from_status: {}, to_status: {}, reason_code: {} },
  },
  weather_fetch_failed: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { provider: {}, cell_id: {}, attempt: {} },
  },
  conditions_snapshot_created: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: { round_id: {}, course_id: {}, fallback: {}, light: {} },
  },
  season_transition_completed: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { season_no: {}, duration_ms: {}, steps_retried: {} },
  },
  season_turn_reached: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { season_no: {}, duration_ms: {}, steps_retried: {} },
  },
  break_week_started: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { season_no: {}, duration_ms: {}, steps_retried: {} },
  },
  calendar_generated: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { season_no: {}, calendar_version: {}, real_majors_aligned: {} },
  },
  major_week_marker_shown: {
    table: 'World, courses and travel',
    src: 'client',
    system: false,
    admin: false,
    properties: { slot_code: {}, season_no: {} },
  },
  closure_booking_refunded: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: { closure_id: {}, booking_kind: {}, currency: {} },
  },
  closure_event_moved: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { closure_id: {}, event_id: {}, outcome: {} },
  },
  course_generated: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: {
      course_id: {},
      style: {},
      hazard_density_score: {},
      green_complexity_score: {},
      similarity_max_score: {},
      vision_result: {},
      is_flagship: {},
      licence_type: {},
      version: {},
      design_method: {},
      source_resolution_m: {},
    },
  },
  course_published: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: {
      course_id: {},
      style: {},
      hazard_density_score: {},
      green_complexity_score: {},
      similarity_max_score: {},
      vision_result: {},
      is_flagship: {},
      licence_type: {},
      version: {},
      design_method: {},
      source_resolution_m: {},
    },
  },
  course_rating_settled: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: {
      course_id: {},
      tee_set: {},
      rounds_counted: {},
      scratch_score_delta: {},
      difficulty_factor_delta: {},
    },
  },
  world_map_opened: {
    table: 'World, courses and travel',
    src: 'client',
    system: false,
    admin: false,
    properties: { map_hub: {}, zoom_level: {}, course_id: {}, pin_state: {} },
  },
  course_pin_tapped: {
    table: 'World, courses and travel',
    src: 'client',
    system: false,
    admin: false,
    properties: { map_hub: {}, zoom_level: {}, course_id: {}, pin_state: {} },
  },
  travel_completed: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      distance_km: {},
      coins_spent: {},
      hours_spent: {},
      jetlag_tier: {},
      reason: {},
      group_size: {},
      fare: {},
      junior: {},
    },
  },
  visitor_round_booked: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: { course_id: {}, fee_coins: {}, visitor_rule_hit: {} },
  },
  real_club_linked: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: { has_home_course: {} },
  },
  passport_stamp_awarded: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: { course_id: {}, is_first_ever_stamp: {} },
  },
  passport_list_completed: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: { list_id: {}, reward_coins: {} },
  },
  site_report_created: {
    table: 'World, courses and travel',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: {
      holes_possible: {},
      elevation_source: {},
      source_resolution_m: {},
      top_suggested_style: {},
      real_holes_nearby: {},
    },
  },
  course_design_checked: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: { design_method: {}, result: {}, failed_rule_count: {}, first_failed_rule: {} },
  },
  course_floodlights_changed: {
    table: 'World, courses and travel',
    src: 'server',
    system: false,
    admin: false,
    properties: { course_id: {}, has_floodlights: {} },
  },
  golfer_potential_raised: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { skill_group: {}, source: {}, amount_band: {}, origin: {} },
  },
  golfer_age_limit_reached: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { skill_group: {} },
  },
  golfer_unretired: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, level_band: {}, route: {}, old_path: {}, new_path: {} },
  },
  golfer_started_over: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, level_band: {}, route: {}, old_path: {}, new_path: {} },
  },
  adaptive_profile_set: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { at_creation: {}, has_profile: {}, profile_class: {} },
  },
  biography_viewed: {
    table: 'The golfer, training and the engine',
    src: 'client',
    system: false,
    admin: false,
    properties: { own: {} },
  },
  biography_entry_hidden: {
    table: 'The golfer, training and the engine',
    src: 'client',
    system: false,
    admin: false,
    properties: { milestone: {} },
  },
  ai_population_rebalanced: {
    table: 'The golfer, training and the engine',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { population: {}, ai_count: {}, human_count: {}, thinned: {} },
  },
  training_plan_suggested: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { heuristic_version: {}, practice_hours: {} },
  },
  training_plan_approved: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { edited: {}, seconds_to_approve: {} },
  },
  training_plan_tweaked: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { blocks_changed: {} },
  },
  workload_warning_shown: {
    table: 'The golfer, training and the engine',
    src: 'client',
    system: false,
    admin: false,
    properties: { zone: {} },
  },
  swing_rebuild_started: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { group: {}, p_success_band: {} },
  },
  swing_rebuild_resolved: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { outcome: {} },
  },
  lesson_booked: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { focus: {} },
  },
  school_choice_set: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { choice: {} },
  },
  habit_week_kept: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { count: {} },
  },
  holiday_started: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { days: {} },
  },
  holiday_ended: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { early: {} },
  },
  real_practice_logged: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { minutes_band: {} },
  },
  training_aid_equipped: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { aid_code: {} },
  },
  round_simulated: {
    table: 'The golfer, training and the engine',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { mode: {}, detail: {}, sim_version: {}, duration_ms: {} },
  },
  replay_verified: {
    table: 'The golfer, training and the engine',
    src: 'server',
    system: false,
    admin: false,
    properties: { match: {} },
  },
  sg_baseline_switched: {
    table: 'The golfer, training and the engine',
    src: 'client',
    system: false,
    admin: false,
    properties: { to: {} },
  },
  handicap_first_issued: {
    table: 'Handicap and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { holes: {}, value: {} },
  },
  supplementary_card_refused: {
    table: 'Handicap and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {} },
  },
  conditions_adjustment_published: {
    table: 'Handicap and fair play',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { ca: {}, n_scores: {}, n_ai_topup: {} },
  },
  handicap_record_viewed: {
    table: 'Handicap and fair play',
    src: 'client',
    system: false,
    admin: false,
    properties: { whose: {} },
  },
  handicap_check_maths_used: {
    table: 'Handicap and fair play',
    src: 'client',
    system: false,
    admin: false,
    properties: { matched: {} },
  },
  committee_decision_issued: {
    table: 'Handicap and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { action: {}, decided_by: {}, minutes_to_decision: {} },
  },
  event_entry_refused: {
    table: 'Handicap and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {} },
  },
  tee_rating_updated: {
    table: 'Handicap and fair play',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { delta_ss: {}, delta_df: {}, provisional: {} },
  },
  kit_effect_viewed: {
    table: 'Handicap and fair play',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  paid_support_effect_viewed: {
    table: 'Handicap and fair play',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  homegrown_confirm_shown: {
    table: 'Handicap and fair play',
    src: 'client; server',
    system: false,
    admin: false,
    properties: { class: {}, route: {} },
  },
  homegrown_confirm_answered: {
    table: 'Handicap and fair play',
    src: 'client; server',
    system: false,
    admin: false,
    properties: { answer: {} },
  },
  homegrown_audit_mismatch_found: {
    table: 'Handicap and fair play',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { source: {} },
  },
  homegrown_league_refused: {
    table: 'Handicap and fair play',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {} },
  },
  club_membership_applied: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, tier: {}, membership_type: {}, outcome: {} },
  },
  club_co_owner_added: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, permission_scopes: {} },
  },
  club_redesign_started: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, scope: {}, budget_tier: {}, build_days: {} },
  },
  club_weekly_accounts_computed: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { club_id: {}, net_coins: {}, at_risk: {} },
  },
  club_satisfaction_computed: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { club_id: {}, score: {}, lowest_component: {} },
  },
  club_caretaker_activated: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { club_id: {}, days_inactive: {}, reason: {}, failed_closes: {} },
  },
  club_forced_sale_completed: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, sale_price_coins: {}, proceeds_recipient_count: {} },
  },
  club_sold: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, method: {}, grassroots_diversion_coins: {} },
  },
  club_promoted: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { club_id: {}, tier: {}, season_id: {} },
  },
  club_relegated: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { club_id: {}, tier: {}, season_id: {} },
  },
  club_extra_opened: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, extra_type: {}, by: {}, price_pct: {} },
  },
  club_extra_closed: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, extra_type: {}, by: {}, price_pct: {} },
  },
  club_extra_price_set: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, extra_type: {}, by: {}, price_pct: {} },
  },
  club_extra_takings_computed: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: {
      club_id: {},
      extra_type: {},
      takings: {},
      real_share: {},
      place_factor: {},
      name_factor: {},
    },
  },
  club_extras_manager_hired: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {} },
  },
  club_extras_manager_released: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {} },
  },
  club_function_booked: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, kind: {}, fee_coins: {}, booked_by: {} },
  },
  club_resort_opened: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, rooms: {} },
  },
  club_team_selected: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, fixture_id: {}, humans: {}, computer_run: {}, picked_by: {} },
  },
  club_team_reply_sent: {
    table: 'Clubs and their leagues',
    src: 'server',
    system: false,
    admin: false,
    properties: { fixture_id: {}, reply: {} },
  },
  club_league_fixture_settled: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: {
      fixture_id: {},
      division: {},
      home_points: {},
      away_points: {},
      human_rounds: {},
      autopilot_rounds: {},
    },
  },
  club_cup_tie_settled: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { fixture_id: {}, cup_round: {}, home_points: {}, away_points: {}, tie_break: {} },
  },
  club_league_prize_paid: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { club_id: {}, division: {}, kind: {}, coins: {} },
  },
  club_season_closed: {
    table: 'Clubs and their leagues',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { club_id: {}, season_id: {}, net_coins_season: {}, promoted: {}, relegated: {} },
  },
  tour_school_stage_completed: {
    table: 'Careers, rounds and roles',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { stage: {}, season_no: {}, site_id: {}, field_size: {} },
  },
  hall_of_fame_inducted: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { category: {}, points: {} },
  },
  ranking_published: {
    table: 'Careers, rounds and roles',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { ranking: {} },
  },
  cup_team_announced: {
    table: 'Careers, rounds and roles',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { cup: {}, season_no: {}, side: {}, humans: {}, ai_golfers: {} },
  },
  cup_lineup_revealed: {
    table: 'Careers, rounds and roles',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { cup: {}, day_no: {}, side: {}, by_default: {} },
  },
  cup_completed: {
    table: 'Careers, rounds and roles',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { cup: {}, season_no: {}, result_side: {}, points_uki: {}, points_usa: {} },
  },
  captain_nominated: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { cup: {}, side: {}, decided_by: {}, is_ai: {}, by_default: {} },
  },
  captain_elected: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { cup: {}, side: {}, decided_by: {}, is_ai: {}, by_default: {} },
  },
  captain_pick_made: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { cup: {}, side: {}, decided_by: {}, is_ai: {}, by_default: {} },
  },
  homegrown_league_joined: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { division_no: {} },
  },
  homegrown_week_scored: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { division_no: {}, season_week: {}, position: {}, autopilot: {} },
  },
  homegrown_division_changed: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { from_division: {}, to_division: {}, reason: {} },
  },
  team_match_completed: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { match_type: {}, live: {}, result: {} },
  },
  match_conceded: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { what: {} },
  },
  competition_settled: {
    table: 'Careers, rounds and roles',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { competition_id: {}, field_size: {}, ai_fill_count: {} },
  },
  player_event_created: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { format: {}, entry_fee_coins: {}, visibility: {} },
  },
  player_event_topup_applied: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { amount_coins: {}, eligible_entrants: {} },
  },
  playoff_started: {
    table: 'Careers, rounds and roles',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { competition_id: {}, tied_count: {} },
  },
  coach_session_booked: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { fee_coins: {}, gain_measured: {}, coach_level: {} },
  },
  coach_session_completed: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { fee_coins: {}, gain_measured: {}, coach_level: {} },
  },
  family_coaching_session_completed: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { fee_coins: {}, gain_measured: {}, coach_level: {} },
  },
  club_manager_shift_completed: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { wage_coins: {} },
  },
  self_dealing_blocked: {
    table: 'Careers, rounds and roles',
    src: 'server (system for the last two)',
    system: false,
    admin: false,
    properties: { protection_number: {}, surface: {} },
  },
  pattern_check_flagged: {
    table: 'Careers, rounds and roles',
    src: 'server (system for the last two)',
    system: true,
    admin: false,
    properties: { role_type: {}, linked_share_pct: {} },
  },
  business_held_for_review: {
    table: 'Careers, rounds and roles',
    src: 'server (system for the last two)',
    system: true,
    admin: false,
    properties: { reason: {} },
  },
  role_head_start_accepted: {
    table: 'Careers, rounds and roles',
    src: 'server',
    system: false,
    admin: false,
    properties: { tier: {} },
  },
  standard_kit_event_entered: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  club_ball_round_played: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  manufacturer_launch_week: {
    table: 'Kit, the economy and payments',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { manufacturer_count: {}, model_count: {} },
  },
  balls_restocked: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: { count: {}, auto: {} },
  },
  refit_advice_muted: {
    table: 'Kit, the economy and payments',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  item_sold_back: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: { mode: {}, category: {}, trade_in_coins: {}, sell_back_coins: {} },
  },
  market_listing_blocked: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {} },
  },
  auction_closed: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: { reserve_met: {}, extensions: {} },
  },
  trade_completed: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  business_sale_completed: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: { g_share: {} },
  },
  sponsor_offer_received: {
    table: 'Kit, the economy and payments',
    src: 'server; the tap is client',
    system: false,
    admin: false,
    properties: { tier: {}, reach: {}, spots: {}, retainer: {} },
  },
  sponsor_counter_sent: {
    table: 'Kit, the economy and payments',
    src: 'server; the tap is client',
    system: false,
    admin: false,
    properties: { ratio: {} },
  },
  sponsor_offer_outcome: {
    table: 'Kit, the economy and payments',
    src: 'server; the tap is client',
    system: false,
    admin: false,
    properties: { outcome: {} },
  },
  sponsor_logo_tapped: {
    table: 'Kit, the economy and payments',
    src: 'server; the tap is client',
    system: false,
    admin: false,
    properties: { spot: {} },
  },
  bank_proposal_decided: {
    table: 'Kit, the economy and payments',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { decision: {} },
  },
  exploit_flag_raised: {
    table: 'Kit, the economy and payments',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { kind: {} },
  },
  penalty_applied: {
    table: 'Kit, the economy and payments',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { penalty_kind: {} },
  },
  shop_viewed: {
    table: 'Kit, the economy and payments',
    src: 'client',
    system: false,
    admin: false,
    properties: { tab: {} },
  },
  product_viewed: {
    table: 'Kit, the economy and payments',
    src: 'client',
    system: false,
    admin: false,
    properties: { product_code: {} },
  },
  subscription_renewed: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  chargeback_received: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  ad_offer_shown: {
    table: 'Kit, the economy and payments',
    src: 'client; server',
    system: false,
    admin: false,
    properties: {},
  },
  ad_reward_completed: {
    table: 'Kit, the economy and payments',
    src: 'client; server',
    system: false,
    admin: false,
    properties: {},
  },
  price_test_exposed: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: { experiment: {}, arm: {} },
  },
  spending_summary_viewed: {
    table: 'Kit, the economy and payments',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  handover_month_granted: {
    table: 'Kit, the economy and payments',
    src: 'server',
    system: false,
    admin: false,
    properties: { route: {} },
  },
  legal_document_viewed: {
    table: 'Law, testers and the launch',
    src: 'client; server',
    system: false,
    admin: false,
    properties: { doc_code: {}, version: {}, layout: {}, method: {} },
  },
  legal_document_accepted: {
    table: 'Law, testers and the launch',
    src: 'client; server',
    system: false,
    admin: false,
    properties: { doc_code: {}, version: {}, layout: {}, method: {} },
  },
  signup_region_refused: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { ip_country: {} },
  },
  dsar_requested: {
    table: 'Law, testers and the launch',
    src: 'server',
    system: false,
    admin: false,
    properties: { request_type: {}, days_to_complete: {} },
  },
  dsar_completed: {
    table: 'Law, testers and the launch',
    src: 'server',
    system: false,
    admin: false,
    properties: { request_type: {}, days_to_complete: {} },
  },
  subscription_reminder_sent: {
    table: 'Law, testers and the launch',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, channel: {} },
  },
  compliance_gate_changed: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { gate_code: {}, is_clear: {} },
  },
  beta_survey_submitted: {
    table: 'Law, testers and the launch',
    src: 'client',
    system: false,
    admin: false,
    properties: { week_number: {}, recommend_score: {}, look_score: {} },
  },
  bug_report_submitted: {
    table: 'Law, testers and the launch',
    src: 'client',
    system: false,
    admin: false,
    properties: { severity_self_rated: {} },
  },
  waitlist_joined: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { source: {}, country: {} },
  },
  marketing_link_clicked: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { channel: {}, campaign: {} },
  },
  beta_invite_sent: {
    table: 'Law, testers and the launch',
    src: 'server',
    system: false,
    admin: false,
    properties: { cohort_code: {}, beta_phase: {} },
  },
  beta_invite_accepted: {
    table: 'Law, testers and the launch',
    src: 'server',
    system: false,
    admin: false,
    properties: { cohort_code: {}, beta_phase: {} },
  },
  partner_referral_signup: {
    table: 'Law, testers and the launch',
    src: 'server',
    system: false,
    admin: false,
    properties: { partner_id: {}, partner_type: {} },
  },
  community_joined: {
    table: 'Law, testers and the launch',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  founding_member_granted: {
    table: 'Law, testers and the launch',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  launch_stage_changed: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { from_stage: {}, to_stage: {} },
  },
  launch_date_booked: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { state: {}, reason: {} },
  },
  launch_go_no_go_recorded: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { decision: {} },
  },
  season_review_completed: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { season_no: {}, kind: {} },
  },
  roadmap_milestone_reached: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { milestone_code: {}, wag: {}, mrr_gbp: {} },
  },
  roadmap_item_status_changed: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { code: {}, from_status: {}, to_status: {} },
  },
  roadmap_item_slipped: {
    table: 'Law, testers and the launch',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { code: {}, from_season_no: {}, from_half: {}, to_season_no: {}, to_half: {} },
  },
  business_staff_hired: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, post: {}, level: {} },
  },
  business_machine_ordered: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, machine: {}, level: {} },
  },
  business_research_started: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, track: {}, level: {} },
  },
  business_price_refused: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {} },
  },
  business_vote_closed: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, result: {} },
  },
  business_flag_raised: {
    table: 'Businesses',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { rule: {} },
  },
  business_sale_listed: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, valuation_band: {} },
  },
  business_ai_rival_changed: {
    table: 'Businesses',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { kind: {}, market: {}, change: {}, player_share_band: {} },
  },
  resort_trip_cancelled: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { by: {}, hours_before: {} },
  },
  resort_group_created: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { source: {}, invited: {} },
  },
  maker_design_submitted: {
    table: 'Businesses',
    src: 'server',
    system: false,
    admin: false,
    properties: { category: {}, band: {}, result: {} },
  },
  golf_export_requested: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      form: { values: ['xlsx', 'csv_zip'] },
      units: { values: ['yards', 'metres'] },
      golfers_count: {},
      range_kind: { values: ['all', 'seasons', 'dates'] },
      from: { values: ['stats_room', 'settings'] },
    },
  },
  golf_export_ready: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      form: {},
      build_seconds_band: { values: ['under_60', '60_to_300', '300_plus'] },
      rows_band: { values: ['under_10k', '10k_to_100k', '100k_plus'] },
      ok: {},
    },
  },
  stats_view_opened: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'client',
    system: false,
    admin: false,
    properties: { view: {}, members_view: {}, example_shown: {}, in_round: {} },
  },
  stats_view_saved: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'server',
    system: false,
    admin: false,
    properties: { view: {} },
  },
  stats_compare_run: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'client',
    system: false,
    admin: false,
    properties: { view: {}, compare_kind: { values: ['seasons', 'dates', 'mark'] } },
  },
  study_session_done: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'server',
    system: false,
    admin: false,
    properties: {
      knowledge_band: { values: ['under_0.10', '0.10_to_0.20', '0.20_to_cap', 'at_cap'] },
      counted: {},
    },
  },
  course_study_measure_used: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'client',
    system: false,
    admin: false,
    properties: { course_id: {}, on_green: {} },
  },
  course_finder_used: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'client',
    system: false,
    admin: false,
    properties: { kind: {}, results_band: {}, from: {} },
  },
  course_book_saved: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'server',
    system: false,
    admin: false,
    properties: { holes_with_route: {}, complete: {}, made_by: { values: ['player', 'caddie'] } },
  },
  caddie_book_ordered: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'server',
    system: false,
    admin: false,
    properties: { caddie_level: {}, fee_coins: {} },
  },
  course_book_used: {
    table: 'The Stats Room, Export my golf, course study and the books',
    src: 'server',
    system: false,
    admin: false,
    properties: { holes_from_book: {}, holes_changed: {} },
  },
  round_energy_posted: {
    table: "The golfer in the sixth rulings: energy, looks, a niggle and the junior's price",
    src: 'server',
    system: false,
    admin: false,
    properties: {
      start_band: {},
      used_band: {},
      foods_eaten: {},
      end_band: {},
      carry_mode: {},
      holes: {},
      reached_zero: {},
    },
  },
  golfer_look_changed: {
    table: "The golfer in the sixth rulings: energy, looks, a niggle and the junior's price",
    src: 'server',
    system: false,
    admin: false,
    properties: { at_creation: {}, parts_changed: {} },
  },
  golfer_swing_style_set: {
    table: "The golfer in the sixth rulings: energy, looks, a niggle and the junior's price",
    src: 'server',
    system: false,
    admin: false,
    properties: { style: {}, at_creation: {}, was_free: {} },
  },
  course_knowledge_capped: {
    table: "The golfer in the sixth rulings: energy, looks, a niggle and the junior's price",
    src: 'server',
    system: false,
    admin: false,
    properties: { source: {}, cap_band: {} },
  },
  niggle_answered: {
    table: "The golfer in the sixth rulings: energy, looks, a niggle and the junior's price",
    src: 'server',
    system: false,
    admin: false,
    properties: { answer: {}, had_injury: {}, zone: {}, day_added: {} },
  },
  junior_price_applied: {
    table: "The golfer in the sixth rulings: energy, looks, a niggle and the junior's price",
    src: 'server',
    system: false,
    admin: false,
    properties: { what: {}, level: {} },
  },
  round_not_counting_shown: {
    table: "The golfer in the sixth rulings: energy, looks, a niggle and the junior's price",
    src: 'client',
    system: false,
    admin: false,
    properties: { reason: {}, place: {} },
  },
  business_track_shelved: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, line: {}, track: {}, levels: {} },
  },
  business_track_brought_back: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { track: {}, levels: {}, coins: {} },
  },
  business_track_retooled: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { track: {}, levels: {}, to_line: {}, credit: {}, reason: {} },
  },
  business_field_started: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field: {}, era: {}, size: {}, tests_done: {} },
  },
  business_field_fitted: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field: {}, category: {}, level: {}, licensed: {} },
  },
  business_field_test_done: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field: {}, kind: {}, test_no: {}, half_width_band: {} },
  },
  business_field_truth_learnt: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field: {}, tests_done: {} },
  },
  business_licence_offered: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field: {}, kind: {}, rate_pct: {}, seasons: {}, reason: {} },
  },
  business_licence_signed: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field: {}, kind: {}, rate_pct: {}, seasons: {}, reason: {} },
  },
  business_licence_ended: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field: {}, kind: {}, rate_pct: {}, seasons: {}, reason: {} },
  },
  business_royalty_paid: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { rate_pct: {}, amount_band: {} },
  },
  business_case_filed: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field: {}, cases_now: {} },
  },
  business_line_added: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { type: {}, line: {} },
  },
  tech_field_opened: {
    table: 'Kit research and the rule-makers',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { field: {}, era: {}, season: {} },
  },
  tech_field_published: {
    table: 'Kit research and the rule-makers',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { field: {}, yield_band: {} },
  },
  tech_patent_started: {
    table: 'Kit research and the rule-makers',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { field: {}, holders: {} },
  },
  maker_legal_version_made: {
    table: 'Kit research and the rule-makers',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { field: {}, ruling: {}, models: {} },
  },
  tradeoff_sum_refused: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { category: {}, sum: {}, allowed: {}, maker_is_ai: {} },
  },
  kit_ruling_proposed: {
    table: 'Kit research and the rule-makers',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { field_key: {}, stance: {}, ruling: {}, in_force_season: {} },
  },
  kit_ruling_final: {
    table: 'Kit research and the rule-makers',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { field_key: {}, ruling: {}, cases: {}, delayed: {}, softened: {} },
  },
  kit_ruling_notice_shown: {
    table: 'Kit research and the rule-makers',
    src: 'client',
    system: false,
    admin: false,
    properties: { field_key: {}, ruling: {}, surface: {} },
  },
  kit_legal_swap: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field_key: {}, category: {}, kept_original: {} },
  },
  kit_original_swapped_later: {
    table: 'Kit research and the rule-makers',
    src: 'server',
    system: false,
    admin: false,
    properties: { field_key: {}, category: {}, kept_original: {} },
  },
  kit_compare_opened: {
    table: 'Kit research and the rule-makers',
    src: 'client; server',
    system: false,
    admin: false,
    properties: { category: {}, models: {} },
  },
  fit_suggestions_shown: {
    table: 'Kit research and the rule-makers',
    src: 'client; server',
    system: false,
    admin: false,
    properties: { slots: {}, fitter_level: {} },
  },
  society_day_marked: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { days_marked: {} },
  },
  society_day_settled: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { counted: {}, finishers_band: {}, flights: {} },
  },
  series_day_settled: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { series_kind: {}, scorers: {}, flights: {} },
  },
  society_season_closed: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { days_counted: {}, flights: {}, members_band: {} },
  },
  society_role_changed: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { role: {}, how: {} },
  },
  society_election_closed: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { role: {}, decided_by: {}, turnout_band: {} },
  },
  society_invite_created: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: {},
  },
  society_invite_joined: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { new_account: {} },
  },
  society_invite_ended: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { reason: {}, uses_band: {} },
  },
  starter_group_offered: {
    table: 'Societies, starter groups and friends',
    src: 'server; the offer is client',
    system: false,
    admin: false,
    properties: { members_band: {}, with_four_rounds: {} },
  },
  starter_group_joined: {
    table: 'Societies, starter groups and friends',
    src: 'server; the offer is client',
    system: false,
    admin: false,
    properties: { members_band: {}, with_four_rounds: {} },
  },
  starter_group_closed: {
    table: 'Societies, starter groups and friends',
    src: 'server; the offer is client',
    system: false,
    admin: false,
    properties: { members_band: {}, with_four_rounds: {} },
  },
  starter_society_founded: {
    table: 'Societies, starter groups and friends',
    src: 'server; the offer is client',
    system: false,
    admin: false,
    properties: { members_band: {}, with_four_rounds: {} },
  },
  friendly_match_challenged: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { holes: {} },
  },
  friendly_match_completed: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { holes: {}, decided_at_hole: {} },
  },
  friendly_match_lapsed: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { rounds_played: {} },
  },
  ledger_viewed: {
    table: 'Societies, starter groups and friends',
    src: 'client; client; server',
    system: false,
    admin: false,
    properties: { has_trophy: {}, matches_band: {} },
  },
  ledger_hidden: {
    table: 'Societies, starter groups and friends',
    src: 'client; client; server',
    system: false,
    admin: false,
    properties: {},
  },
  ledger_trophy_named: {
    table: 'Societies, starter groups and friends',
    src: 'client; client; server',
    system: false,
    admin: false,
    properties: { preset: {} },
  },
  old_rival_viewed: {
    table: 'Societies, starter groups and friends',
    src: 'client',
    system: false,
    admin: false,
    properties: { visibility: {} },
  },
  media_visibility_changed: {
    table: 'Societies, starter groups and friends',
    src: 'server',
    system: false,
    admin: false,
    properties: { to: {}, by: {} },
  },
  daily_done_reached: {
    table: 'Societies, starter groups and friends',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  club_calendar_slot_changed: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, event_template: {}, action: {}, by: {} },
  },
  course_record_set: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { course_id: {}, tee_set_id: {}, gross_score: {}, record_no: {}, is_ai: {} },
  },
  club_campaign_vote_cast: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, season_id: {}, question: {}, changed: {} },
  },
  club_campaign_goal_set: {
    table: 'The club and the golf world',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: {
      club_id: {},
      season_id: {},
      goal_code: {},
      target: {},
      human_members: {},
      votes_cast: {},
    },
  },
  club_campaign_ended: {
    table: 'The club and the golf world',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: {
      club_id: {},
      season_id: {},
      goal_code: {},
      progress: {},
      target: {},
      reached: {},
      reached_on_season_day: {},
    },
  },
  club_hole_in_one_recorded: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, hole_no: {}, round_kind: {}, is_ai: {} },
  },
  club_treat_offered: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, coins: {} },
  },
  club_treat_paid: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, coins: {} },
  },
  club_signature_hole_set: {
    table: 'The club and the golf world',
    src: 'server (system)',
    system: true,
    admin: false,
    properties: { club_id: {}, season_id: {}, hole_no: {}, votes_cast: {}, by: {} },
  },
  club_cabinet_item_shown: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, keepsake_kind: {}, by: {} },
  },
  club_cabinet_item_removed: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { club_id: {}, keepsake_kind: {}, by: {} },
  },
  rollup_joined: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { slot: {}, format: {} },
  },
  rollup_played: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { group_humans: {}, by_caddie: {}, declared: {} },
  },
  range_night_go: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { bold_attempts: {}, total: {}, via_link: {} },
  },
  proam_answered: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { answer: {}, has_obligation: {} },
  },
  proam_joined: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { route: {} },
  },
  proam_played: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { role: {}, route: {}, by_caddie: {} },
  },
  medical_withdrawal: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { hole: {}, counted: {} },
  },
  course_design_credited: {
    table: 'The club and the golf world',
    src: 'server',
    system: false,
    admin: false,
    properties: { kind: {}, how: {}, shaped_by_hand: {}, designers: {} },
  },
  made_by_members_course_opened: {
    table: 'The club and the golf world',
    src: 'client',
    system: false,
    admin: false,
    properties: { order: {}, position: {} },
  },
  first_expectation_set: {
    table: 'The habit loop',
    src: 'client',
    system: false,
    admin: false,
    properties: {
      choice: { values: ['range', 'study', 'rest'] },
      expectation: { values: ['better', 'same', 'unsure'] },
    },
  },
  primary_decision_shown: {
    table: 'The habit loop',
    src: 'client',
    system: false,
    admin: false,
    properties: { kind: { values: ['challenge', 'prepare', 'moment', 'plan', 'none'] } },
  },
  society_week_viewed: {
    table: 'The habit loop',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  challenge_route_chosen: {
    table: 'The habit loop',
    src: 'client',
    system: false,
    admin: false,
    properties: { route: { values: ['own_golfer', 'join_society', 'not_now'] } },
  },
  interests_changed: {
    table: 'The habit loop',
    src: 'client',
    system: false,
    admin: false,
    properties: { field: { values: ['familiarity', 'motivation', 'focus', 'explain_style'] } },
  },
  interests_reset: {
    table: 'The habit loop',
    src: 'client',
    system: false,
    admin: false,
    properties: {},
  },
  first_minute_offered: {
    table: 'The first hole (ANA-313)',
    src: '',
    system: false,
    admin: false,
    properties: { entry: { values: ['guest', 'sign_up'] } },
  },
  first_minute_answered: {
    table: 'The first hole (ANA-313)',
    src: '',
    system: false,
    admin: false,
    properties: { answer: { values: ['range', 'study', 'rest'] }, ms_since_first_frame: {} },
  },
  first_minute_report_shown: {
    table: 'The first hole (ANA-313)',
    src: '',
    system: false,
    admin: false,
    properties: { ms_since_first_frame: {}, place: {}, replay_quoted: {} },
  },
  first_minute_twist_shown: {
    table: 'The first hole (ANA-313)',
    src: '',
    system: false,
    admin: false,
    properties: { answers_compared: {} },
  },
  first_minute_skipped: {
    table: 'The first hole (ANA-313)',
    src: '',
    system: false,
    admin: false,
    properties: {},
  },
  experiment_exposed: {
    table: 'Analytics events (system-level), 27 §9',
    src: '',
    system: false,
    admin: false,
    properties: {},
  },
  consent_updated: {
    table: 'Analytics events (system-level), 27 §9',
    src: '',
    system: false,
    admin: false,
    properties: { purpose: {}, granted: {}, legal_region: {} },
  },
  funnel_step_reached: {
    table: 'Analytics events (system-level), 27 §9',
    src: '',
    system: false,
    admin: false,
    properties: { funnel_key: {}, step: {} },
  },
  data_deletion_requested: {
    table: 'Analytics events (system-level), 27 §9',
    src: '',
    system: false,
    admin: false,
    properties: {},
  },
  schema_violation_detected: {
    table: 'Analytics events (system-level), 27 §9',
    src: '',
    system: true,
    admin: false,
    properties: {},
  },
} as const satisfies Readonly<Record<string, EventSpec>>;

/** The names the schema refuses (ANA-311 a; "Names withdrawn", "Older names withdrawn"; ANA-T21). */
export const WITHDRAWN_EVENT_NAMES = [
  'show_play_started',
  'show_progress',
  'show_episode_started',
  'show_episode_progress',
  'recap_viewed',
  'recap_shared',
  'week_in_golf_viewed',
  'week_in_golf_shared',
  'news_story_viewed',
  'post_created',
  'post_liked',
  'comment_created',
  'first_hole_offered',
  'first_hole_started',
  'first_hole_shot_shown',
  'first_hole_finished',
  'first_hole_skipped',
  'friend_request_resolved',
  'wellbeing_break_reminder_shown',
  'caddie_chosen',
  'account_deletion_started',
  'account_deletion_undone',
  'account_deletion_completed',
  'parent_link_created',
  'parent_link_verified',
  'appeal_decided',
  'account_deleted',
  'disaster_closure_started',
  'disaster_closure_lifted',
  'golfer_potential_viewed',
  'family_golfer_created',
  'training_day_processed',
  'rested_bonus_credited',
  'injury_healed',
  'sg_unlocked',
  'why_explainer_opened',
  'what_else_opened',
  'handicap_updated',
  'handicap_flag_raised',
  'handicap_appeal_opened',
  'handicap_appeal_resolved',
  'homegrown_badge_ended',
  'club_taken_over',
  'club_facility_upgraded',
  'club_handed_over',
  'league_promoted',
  'league_relegated',
  'tour_card_awarded',
  'prize_payout_posted',
  'pro_start_created',
  'family_legacy_started',
  'decision_presented',
  'decision_defaulted',
  'hole_plan_chosen',
  'read_taken',
  'shot_committed',
  'pace_warning_issued',
  'caddie_upgraded',
  'practice_advice_added',
  'friend_invite_sent',
  'friend_invite_accepted',
  'competition_entered',
  'role_added',
  'role_dropped',
  'equipment_purchased',
  'shop_item_bought',
  'equipment_fitted',
  'refit_completed',
  'equipment_traded_in',
  'kit_wear_warning_shown',
  'kit_repaired',
  'player_model_submitted',
  'market_listing_created',
  'market_item_sold',
  'economy_dashboard_viewed',
  'refund_processed',
  'self_limit_set',
  'consent_changed',
  'age_gate_result',
  'beta_survey_completed',
  'ui_preferences_changed',
  'hole_map_intro_skipped',
  'read_animation_skipped',
] as const;

/** Property names withdrawn for another name (the "Properties:" row of "Names withdrawn"). */
export const WITHDRAWN_PROPERTY_NAMES = [
  'object_type',
  'content_type',
  'reason',
  'old_tier',
  'new_tier',
  'channel_type',
  'approved_by',
] as const;

/** Names that stay domain events of `events_outbox` and are never sent to analytics (ANA-311 i). */
export const NOT_ANALYTICS_EVENT_NAMES = [
  'coins_earned',
  'coins_spent',
  'aces_spent',
  'ranking_updated',
  'kit_effect_computed',
  'entry.prepared',
  'round.hole_revealed',
  'round.explained',
  'round.report_ready',
  'test.calibration_run_completed',
  'test.golden_vector_failed',
  'test.load_test_completed',
  'test.ai_eval_completed',
  'test.proof_run_completed',
] as const;

/** The super-properties that every client event carries (`24` §9; `27` "The design layer"). */
export const CLIENT_SUPER_PROPERTIES = [
  'theme',
  'view_mode',
  'pro_view',
  'text_scale_bucket',
  'reduced_motion',
  'cvd',
  'font',
  'map_style',
  'calm',
] as const;
